import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import {
  consentementARepondre,
  enregistrerConsentement,
  lireConsentement,
} from "@/lib/consentement";

/**
 * Bannière de consentement aux témoins de pistage — Loi 25 (Québec).
 *
 * Trois choix de conception qui ne sont pas cosmétiques :
 *
 * 1. « Refuser » et « Accepter » ont le MÊME poids visuel — même taille, même
 *    style. La loi exige que refuser soit aussi facile qu'accepter ; le bouton
 *    gris minuscule à côté du gros bouton coloré est précisément ce qui fait
 *    tomber les bannières en défaut.
 * 2. Aucune fermeture par la croix ou par un clic à côté. Fermer sans répondre
 *    laisserait le visiteur croire qu'il a tranché ; ici on ne piste pas tant
 *    qu'il n'a pas dit oui, donc l'absence de réponse est déjà un refus — mais
 *    la question doit rester posée.
 * 3. On nomme Meta et on dit à quoi ça sert. « Nous utilisons des témoins pour
 *    améliorer votre expérience » n'informe personne et ne vaut pas
 *    consentement éclairé.
 *
 * Ni le site, ni les soumissions, ni le compte ne dépendent de la réponse :
 * refuser n'enlève rien au visiteur. C'est dit dans le texte, parce qu'un
 * consentement obtenu sous la menace d'une perte de service n'est pas libre.
 */
const T = {
  fr: {
    titre: "Témoins de mesure",
    corps:
      "Nous aimerions déposer deux témoins : Meta (Facebook), pour savoir quelles annonces amènent des couvreurs jusqu'ici, et Google Analytics, pour savoir quelles pages sont lues. Ni l'un ni l'autre n'est nécessaire au fonctionnement du site.",
    rassurance:
      "Refuser ne vous enlève rien : l'estimateur, les soumissions et votre compte fonctionnent exactement pareil.",
    politique: "Notre politique de confidentialité",
    refuser: "Refuser",
    accepter: "Accepter",
    versPolitique: "/confidentialite",
  },
  en: {
    titre: "Measurement cookies",
    corps:
      "We'd like to set two cookies: Meta (Facebook), to see which ads bring roofers here, and Google Analytics, to see which pages get read. Neither is needed for the site to work.",
    rassurance:
      "Declining costs you nothing: the estimator, the quotes and your account work exactly the same.",
    politique: "Our privacy policy",
    refuser: "Decline",
    accepter: "Accept",
    versPolitique: "/privacy",
  },
  es: {
    titre: "Cookies de medición",
    corps:
      "Quisiéramos colocar dos cookies: Meta (Facebook), para saber qué anuncios traen techadores hasta aquí, y Google Analytics, para saber qué páginas se leen. Ninguna es necesaria para que el sitio funcione.",
    rassurance:
      "Rechazar no le quita nada: el estimador, las cotizaciones y su cuenta funcionan exactamente igual.",
    politique: "Nuestra política de privacidad",
    refuser: "Rechazar",
    accepter: "Aceptar",
    versPolitique: "/privacy",
  },
};

export default function BanniereTemoins() {
  const { lang } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Au premier rendu seulement : si la question a déjà été tranchée, la
    // bannière ne réapparaît jamais d'elle-même.
    setVisible(consentementARepondre());
    // Le lien « Témoins » du pied de page repose la question sans recharger.
    const surChangement = () => setVisible(consentementARepondre());
    window.addEventListener("rb:consentement", surChangement);
    return () => window.removeEventListener("rb:consentement", surChangement);
  }, []);

  if (!visible) return null;

  const L = T[lang] || T.en;

  const repondre = (choix) => {
    const avant = lireConsentement()?.choix;
    enregistrerConsentement(choix);
    setVisible(false);
    // Passer de « oui » à « non » ne peut pas décharger le script Meta déjà en
    // mémoire : seul un rechargement le fait vraiment disparaître. Le retrait
    // doit être réel, pas seulement enregistré.
    if (avant === "oui" && choix === "non") window.location.reload();
    else if (choix === "oui") {
      // Imports tardifs : les modules de mesure ne sont pas évalués avant le oui.
      import("@/lib/pixel").then((m) => { m.initPixel(); m.pageVue(); });
      import("@/lib/analytics").then((m) => { m.initAnalytics(); m.vuePage(); });
    }
  };

  return (
    <div
      data-testid="banniere-temoins"
      role="dialog"
      aria-modal="false"
      aria-label={L.titre}
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-line-strong bg-surface shadow-carte p-5 sm:p-6">
        <h2 className="font-heading font-bold text-lg tracking-tight text-ink">{L.titre}</h2>
        <p className="text-sm text-ink-soft mt-2">{L.corps}</p>
        <p className="text-sm text-ink-soft mt-2">{L.rassurance}</p>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 mt-5">
          <Link
            to={L.versPolitique}
            className="text-sm font-semibold text-brand-ink hover:underline"
          >
            {L.politique}
          </Link>
          {/* Deux boutons de poids strictement identique : c'est la condition
              d'un refus aussi facile que l'acceptation. */}
          <div className="flex gap-3">
            <Button
              onClick={() => repondre("non")}
              data-testid="temoins-refuser"
              className="flex-1 sm:flex-none sm:min-w-[7.5rem] h-11 px-6 rounded-md font-semibold
                         bg-hover-strong text-ink hover:bg-line-strong"
            >
              {L.refuser}
            </Button>
            <Button
              onClick={() => repondre("oui")}
              data-testid="temoins-accepter"
              className="flex-1 sm:flex-none sm:min-w-[7.5rem] h-11 px-6 rounded-md font-semibold
                         bg-hover-strong text-ink hover:bg-line-strong"
            >
              {L.accepter}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
